import { PDFDocument, rgb, StandardFonts, PDFPage, PDFFont } from "pdf-lib";

type Sig = {
  signerName?: string;
  signerEmail?: string;
  driverEmail?: string;
  identityNumber?: string;
  signatureData?: string;
  signedAt?: string;
  emailVerifiedAt?: string;
  geo?: { lat?: number; lng?: number; accuracy?: number; capturedAt?: string };
  attachments?: { originalName?: string; url?: string }[];
};

type PohData = {
  _id: string;
  pohLevel?: 0 | 1 | 2;
  createdAt?: string;
  updatedAt?: string;
  shipmentId?: {
    shipmentNumber?: string;
    referenceNumber?: string;
    status?: string;
    pickup?: any;
    delivery?: any;
    totalQuantity?: number;
    totalWeight?: number;
    loadType?: string;
    pohStatusOverrides?: {
      reason?: string;
      byName?: string;
      at?: string;
      fromStatus?: string;
      toStatus?: string;
    }[];
  };
  pickup?: {
    consignerSignature?: Sig;
    driverSignature?: Sig;
    status?: string;
    completedAt?: string;
    lockedAt?: string;
  };
  delivery?: {
    customerSignature?: Sig;
    driverSignature?: Sig;
    status?: string;
    completedAt?: string;
    lockedAt?: string;
  };
};

// ── ASCII sanitiser ──────────────────────────────────────────────────────────
const sanitize = (s: string) =>
  s
    .replace(/[→➜➔]/g, "->")
    .replace(/[←]/g, "<-")
    .replace(/[—–]/g, "-")
    .replace(/[…]/g, "...")
    .replace(/[·•]/g, "*")
    .replace(/[±]/g, "+/-")
    .replace(/[""]/g, '"')
    .replace(/['']/g, "'")
    .replace(/[^\x20-\x7E\xA0-\xFF]/g, "");

const isObjectId = (s: any) => typeof s === "string" && /^[a-f0-9]{24}$/i.test(s);

function safe<T>(fn: () => T, fallback = "-"): string {
  try {
    const v = fn();
    if (v === undefined || v === null || v === "") return fallback;
    return sanitize(String(v));
  } catch {
    return fallback;
  }
}

const pohRef = (id: string) => `POH-${id.slice(-8).toUpperCase()}`;

// English labels for manual status-advance reasons (the PDF is English-only).
// Mirrors the `statusChange.skipped.*` i18n keys used in the UI.
const OVERRIDE_LABELS: Record<string, string> = {
  pickup_files_skipped: "Driver pickup files not uploaded",
  delivery_files_skipped: "Driver delivery files not uploaded",
  pickup_driver_skipped: "Driver pickup signature not captured",
  journey_start_manual: "Journey started manually (no driver action)",
  delivery_signatures_skipped:
    "Recipient & driver delivery signatures not captured",
  delivery_consignee_skipped: "Consignee delivery signature not captured",
};

const fmtDt = (d?: string) =>
  d ? new Date(d).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "-";

const fmtGeo = (g?: Sig["geo"]) =>
  g && g.lat != null && g.lng != null
    ? `${g.lat.toFixed(5)}, ${g.lng.toFixed(5)}${g.accuracy ? ` (+/-${Math.round(g.accuracy)}m)` : ""}`
    : "-";

// Resolve address into readable lines; skip any ObjectId fragments
const addrLines = (a: any): string[] => {
  if (!a) return ["-"];
  const company = a.companyName && !isObjectId(a.companyName) ? a.companyName : null;
  const street = a.address?.street && !isObjectId(a.address.street) ? a.address.street : null;
  const cityRaw = a.address?.city && !isObjectId(a.address.city) ? a.address.city : null;
  // country might be populated object, unpopulated ObjectId string, or a country code/name
  const countryVal =
    a.address?.country?.name ||
    (typeof a.address?.country === "string" && !isObjectId(a.address.country)
      ? a.address.country
      : null);
  const cityLine = [cityRaw, countryVal].filter(Boolean).join(", ");
  return [company, street, cityLine].filter(Boolean).map(sanitize);
};

async function embedSignaturePng(pdfDoc: PDFDocument, data?: string) {
  if (!data || !data.startsWith("data:image/")) return null;
  try {
    const base64 = data.split(",")[1];
    const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0));
    if (data.includes("image/png")) return await pdfDoc.embedPng(bytes);
    if (data.includes("image/jpeg") || data.includes("image/jpg")) return await pdfDoc.embedJpg(bytes);
    return await pdfDoc.embedPng(bytes);
  } catch {
    return null;
  }
}

type Ctx = {
  page: PDFPage;
  pdfDoc: PDFDocument;
  font: PDFFont;
  bold: PDFFont;
  width: number;
  height: number;
  margin: number;
};

const COLORS = {
  text: rgb(0.13, 0.16, 0.22),
  muted: rgb(0.42, 0.45, 0.5),
  faint: rgb(0.62, 0.65, 0.7),
  line: rgb(0.9, 0.91, 0.93),
  bgSoft: rgb(0.96, 0.97, 0.98),
  blue: rgb(0.149, 0.388, 0.92),
  green: rgb(0.13, 0.55, 0.13),
  greenSoft: rgb(0.85, 0.95, 0.85),
  amber: rgb(0.85, 0.6, 0.13),
  amberSoft: rgb(1, 0.94, 0.78),
  red: rgb(0.86, 0.14, 0.14),
  white: rgb(1, 1, 1),
};

const drawText = (
  ctx: Ctx,
  text: string,
  opts: { x: number; y: number; size: number; bold?: boolean; color?: any; maxWidth?: number },
) => {
  ctx.page.drawText(sanitize(text), {
    x: opts.x,
    y: opts.y,
    size: opts.size,
    font: opts.bold ? ctx.bold : ctx.font,
    color: opts.color ?? COLORS.text,
    maxWidth: opts.maxWidth,
  });
};

const drawRect = (
  ctx: Ctx,
  opts: { x: number; y: number; width: number; height: number; color?: any; borderColor?: any; borderWidth?: number },
) => {
  ctx.page.drawRectangle({
    x: opts.x,
    y: opts.y,
    width: opts.width,
    height: opts.height,
    color: opts.color,
    borderColor: opts.borderColor,
    borderWidth: opts.borderWidth,
  });
};

const drawPill = (
  ctx: Ctx,
  text: string,
  x: number,
  y: number,
  bg: any,
  fg: any,
  size = 8,
) => {
  const t = sanitize(text);
  const w = ctx.bold.widthOfTextAtSize(t, size) + 10;
  drawRect(ctx, { x, y: y - 3, width: w, height: size + 6, color: bg });
  drawText(ctx, t, { x: x + 5, y, size, bold: true, color: fg });
  return w;
};

// Wrap text into lines that fit a max width
const wrapText = (font: PDFFont, text: string, size: number, maxWidth: number): string[] => {
  if (!text) return [];
  const words = sanitize(text).split(/\s+/);
  const lines: string[] = [];
  let current = "";
  for (const word of words) {
    const tentative = current ? `${current} ${word}` : word;
    if (font.widthOfTextAtSize(tentative, size) <= maxWidth) {
      current = tentative;
    } else {
      if (current) lines.push(current);
      // Hard-break if a single word still overflows
      if (font.widthOfTextAtSize(word, size) > maxWidth) {
        let chunk = "";
        for (const ch of word) {
          if (font.widthOfTextAtSize(chunk + ch, size) > maxWidth) {
            if (chunk) lines.push(chunk);
            chunk = ch;
          } else {
            chunk += ch;
          }
        }
        current = chunk;
      } else {
        current = word;
      }
    }
  }
  if (current) lines.push(current);
  return lines;
};

// ── Main ────────────────────────────────────────────────────────────────────
export async function createPOHDocumentPdf(data: PohData): Promise<Uint8Array> {
  const pdfDoc = await PDFDocument.create();
  pdfDoc.setTitle(`Proof of Handover - ${pohRef(data._id)}`);
  pdfDoc.setAuthor("LiveCargo");
  pdfDoc.setSubject("Proof of Handover Document");
  pdfDoc.setCreationDate(new Date());

  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  const bold = await pdfDoc.embedFont(StandardFonts.HelveticaBold);

  let page = pdfDoc.addPage([595, 842]);
  const { width, height } = page.getSize();
  const margin = 52;
  let ctx: Ctx = { page, pdfDoc, font, bold, width, height, margin };
  let y = height - margin;

  const shipment = data.shipmentId;
  const pickup = data.pickup;
  const delivery = data.delivery;
  const pickupComplete = pickup?.status === "completed";
  const deliveryComplete = delivery?.status === "completed";
  const fullyComplete = pickupComplete && deliveryComplete;

  const ensureSpace = (need: number) => {
    if (y - need < margin + 30) {
      page = pdfDoc.addPage([595, 842]);
      ctx = { page, pdfDoc, font, bold, width, height, margin };
      y = height - margin;
    }
  };

  // ── Header card ──
  const headerH = 80;
  drawRect(ctx, { x: margin, y: y - headerH, width: width - 2 * margin, height: headerH, color: COLORS.white, borderColor: COLORS.line, borderWidth: 1 });

  drawRect(ctx, { x: margin + 14, y: y - 34, width: 28, height: 28, color: COLORS.blue });
  drawText(ctx, "LC", { x: margin + 19, y: y - 27, size: 13, bold: true, color: COLORS.white });
  drawText(ctx, "Live Cargo", { x: margin + 48, y: y - 26, size: 16, bold: true });

  drawText(ctx, "PROOF OF HANDOVER", { x: margin + 14, y: y - 48, size: 7, bold: true, color: COLORS.faint });
  drawText(ctx, pohRef(data._id), { x: margin + 14, y: y - 64, size: 18, bold: true });

  const metaX = margin + 230;
  const metaY = y - 30;
  if (shipment?.shipmentNumber) {
    drawText(ctx, `Order #${shipment.shipmentNumber}`, { x: metaX, y: metaY, size: 9, color: COLORS.muted });
  }
  if (shipment?.referenceNumber) {
    drawText(ctx, `Ref: ${shipment.referenceNumber}`, { x: metaX, y: metaY - 12, size: 9, color: COLORS.muted });
  }

  if (fullyComplete) {
    drawPill(ctx, "COMPLETE & LOCKED", metaX, metaY - 30, COLORS.greenSoft, COLORS.green, 8);
  } else {
    drawPill(ctx, "IN PROGRESS", metaX, metaY - 30, COLORS.amberSoft, COLORS.amber, 8);
  }

  y -= headerH + 16;

  // ── Route summary (responsive: dynamic height based on wrapped lines) ──
  const colW = (width - 2 * margin) / 3;
  const innerColW = colW - 24; // 12 padding each side
  const pickupLines = addrLines(shipment?.pickup);
  const deliveryLines = addrLines(shipment?.delivery);

  const wrapAll = (lines: string[], primaryFirst = true) =>
    lines.flatMap((l, i) =>
      wrapText(primaryFirst && i === 0 ? bold : font, l, primaryFirst && i === 0 ? 9 : 8, innerColW),
    );
  const pickupWrapped = wrapAll(pickupLines);
  const deliveryWrapped = wrapAll(deliveryLines);

  const cargoBits: { text: string; size: number; bold?: boolean; color?: any }[] = [];
  if (shipment?.totalQuantity != null) cargoBits.push({ text: `${shipment.totalQuantity} item(s)`, size: 9, bold: true });
  if (shipment?.totalWeight != null) cargoBits.push({ text: `${shipment.totalWeight} kg`, size: 8, color: COLORS.muted });
  if (shipment?.loadType) cargoBits.push({ text: String(shipment.loadType), size: 8, color: COLORS.muted });

  // Compute heights: each col header (~22) + ~11pt per wrapped line
  const colHeight = (lines: string[]) => 28 + lines.length * 11;
  const cargoCount = cargoBits.length || 1;
  const heights = [colHeight(pickupWrapped), colHeight(deliveryWrapped), 28 + cargoCount * 12];
  const routeH = Math.max(...heights, 64) + 10;

  ensureSpace(routeH);
  drawRect(ctx, { x: margin, y: y - routeH, width: width - 2 * margin, height: routeH, color: COLORS.bgSoft });

  const drawColHeader = (label: string, color: any, x: number) => {
    drawText(ctx, label, { x: x + 12, y: y - 14, size: 7, bold: true, color: COLORS.faint });
    drawRect(ctx, { x: x + 12, y: y - 18, width: 4, height: 4, color });
  };
  drawColHeader("PICKUP / FROM", COLORS.green, margin);
  drawColHeader("DELIVERY / TO", COLORS.red, margin + colW);
  drawColHeader("CARGO", COLORS.blue, margin + 2 * colW);

  const drawWrappedCol = (lines: string[], x: number) => {
    let cy = y - 28;
    lines.forEach((l, i) => {
      // Heuristic: the first wrapped line is the bolded company; subsequent lines are muted street/city
      const isCompany = i === 0;
      drawText(ctx, l, {
        x: x + 12,
        y: cy,
        size: isCompany ? 9 : 8,
        bold: isCompany,
        color: isCompany ? COLORS.text : COLORS.muted,
        maxWidth: innerColW,
      });
      cy -= isCompany ? 12 : 10;
    });
  };

  drawWrappedCol(pickupWrapped, margin);
  drawWrappedCol(deliveryWrapped, margin + colW);

  let cargoY = y - 28;
  cargoBits.forEach((b) => {
    drawText(ctx, b.text, {
      x: margin + 2 * colW + 12,
      y: cargoY,
      size: b.size,
      bold: !!b.bold,
      color: b.color ?? COLORS.text,
      maxWidth: innerColW,
    });
    cargoY -= 12;
  });
  if (!cargoBits.length) {
    drawText(ctx, "-", { x: margin + 2 * colW + 12, y: cargoY, size: 8, color: COLORS.muted });
  }

  y -= routeH + 18;

  // ── Slot rendering — computes own height; name BELOW sig area inside the card ──
  const drawSlot = async (
    role: string,
    sig: Sig | undefined,
    x: number,
    w: number,
  ): Promise<number> => {
    const isSigned = !!sig?.signatureData && !!sig?.signerName;

    // Pre-compute the rows so the card height fits its content
    const rows: [string, string][] = [
      ["Signed", isSigned ? fmtDt(sig?.signedAt) : "-"],
      ["Email", safe(() => sig?.signerEmail || sig?.driverEmail)],
      ["ID No.", safe(() => sig?.identityNumber)],
      ["Geo", fmtGeo(sig?.geo)],
      ["Proofs", `${sig?.attachments?.length ?? 0} file(s)`],
    ];

    const padX = 12;
    const sigH = 58;
    const roleH = 14;
    const sigNameH = 14;
    const pillRowH = 14;
    const rowsH = rows.length * 11;
    const bottomPad = 8;
    const cardH = roleH + sigH + 6 + sigNameH + 4 + pillRowH + 4 + rowsH + bottomPad;

    // Card frame — all white inside (no gray inner sub-box)
    drawRect(ctx, {
      x,
      y: y - cardH,
      width: w,
      height: cardH,
      color: COLORS.white,
      borderColor: COLORS.line,
      borderWidth: 0.75,
    });

    let cy = y - 14;
    drawText(ctx, role.toUpperCase(), { x: x + padX, y: cy, size: 7, bold: true, color: COLORS.faint });
    cy -= 6;

    // White signature area (no inner gray box). Bottom line under the signature.
    const sigBottom = cy - sigH;
    const img = await embedSignaturePng(pdfDoc, sig?.signatureData);
    if (img) {
      const maxW = w - 2 * padX;
      const maxH = sigH - 6;
      const ratio = Math.min(maxW / img.width, maxH / img.height);
      const dw = img.width * ratio;
      const dh = img.height * ratio;
      ctx.page.drawImage(img, {
        x: x + (w - dw) / 2,
        y: sigBottom + (sigH - dh) / 2,
        width: dw,
        height: dh,
      });
    } else {
      const placeholder = "- pending -";
      const pw = font.widthOfTextAtSize(placeholder, 9);
      drawText(ctx, placeholder, {
        x: x + (w - pw) / 2,
        y: sigBottom + sigH / 2 - 3,
        size: 9,
        color: COLORS.faint,
      });
    }
    // Bottom line beneath the signature area
    ctx.page.drawLine({
      start: { x: x + padX, y: sigBottom },
      end: { x: x + w - padX, y: sigBottom },
      thickness: 0.75,
      color: COLORS.line,
    });
    cy = sigBottom - 12;

    // Name caption BELOW the signature area, inside the card
    const name = sig?.signerName ? sanitize(sig.signerName) : "(unsigned)";
    const nameWidth = bold.widthOfTextAtSize(name, 9);
    drawText(ctx, name, {
      x: x + (w - nameWidth) / 2,
      y: cy,
      size: 9,
      bold: true,
      color: isSigned ? COLORS.text : COLORS.faint,
    });
    cy -= 12;

    // Status pill
    drawPill(
      ctx,
      isSigned ? "SIGNED" : "PENDING",
      x + padX,
      cy,
      isSigned ? COLORS.greenSoft : COLORS.line,
      isSigned ? COLORS.green : COLORS.muted,
      7,
    );
    cy -= 12;

    // Detail rows
    for (const [k, v] of rows) {
      drawText(ctx, k, { x: x + padX, y: cy, size: 7, color: COLORS.faint });
      const valTrim = v.length > 38 ? v.slice(0, 36) + "..." : v;
      drawText(ctx, valTrim, { x: x + padX + 32, y: cy, size: 7, bold: true, maxWidth: w - padX * 2 - 32 });
      cy -= 11;
    }

    return cardH;
  };

  const drawEvent = async (
    eventLabel: string,
    pendingMsg: string,
    leftRole: string,
    leftSig: Sig | undefined,
    rightRole: string | null,
    rightSig: Sig | undefined,
    isComplete: boolean,
    lockedAt?: string,
  ) => {
    ensureSpace(220);

    drawText(ctx, sanitize(eventLabel), { x: margin, y: y, size: 9, bold: true });
    const stat = isComplete ? `LOCKED ${fmtDt(lockedAt)}` : pendingMsg;
    const statW = bold.widthOfTextAtSize(sanitize(stat), 7) + 10;
    drawPill(
      ctx,
      stat,
      width - margin - statW,
      y,
      isComplete ? COLORS.greenSoft : COLORS.amberSoft,
      isComplete ? COLORS.green : COLORS.amber,
      7,
    );
    y -= 14;

    if (rightRole == null) {
      // Single-party event (Level 2) — one full-width slot.
      const hL = await drawSlot(leftRole, leftSig, margin, width - 2 * margin);
      y -= hL + 14;
    } else {
      const cardW = (width - 2 * margin - 12) / 2;
      const hL = await drawSlot(leftRole, leftSig, margin, cardW);
      const hR = await drawSlot(rightRole, rightSig, margin + cardW + 12, cardW);
      y -= Math.max(hL, hR) + 14;
    }
  };

  // Level 2 is shipper + consignee only — no driver signatures on either event.
  const hideDriver = data.pohLevel === 2;

  if (hideDriver) {
    await drawEvent(
      "EVENT 1 - PICKUP HANDOVER (Consignor)",
      "Awaiting signature at warehouse",
      "Consignor / Sender",
      pickup?.consignerSignature,
      null,
      undefined,
      pickupComplete,
      pickup?.lockedAt,
    );

    await drawEvent(
      "EVENT 2 - DELIVERY HANDOVER (Consignee)",
      "Awaiting signature at recipient",
      "Consignee / Recipient",
      delivery?.customerSignature,
      null,
      undefined,
      deliveryComplete,
      delivery?.lockedAt,
    );
  } else {
    await drawEvent(
      "EVENT 1 - PICKUP HANDOVER (Consignor -> Driver)",
      "Awaiting signatures at warehouse",
      "Consignor / Sender",
      pickup?.consignerSignature,
      "Pickup Driver",
      pickup?.driverSignature,
      pickupComplete,
      pickup?.lockedAt,
    );

    await drawEvent(
      "EVENT 2 - DELIVERY HANDOVER (Driver -> Consignee)",
      "Awaiting signatures at recipient",
      "Delivery Driver",
      delivery?.driverSignature,
      "Consignee / Recipient",
      delivery?.customerSignature,
      deliveryComplete,
      delivery?.lockedAt,
    );
  }

  // ── Attachments index ──
  const allAtts: { source: string; name: string }[] = [];
  pickup?.consignerSignature?.attachments?.forEach((a) => allAtts.push({ source: "Consignor", name: a.originalName || "-" }));
  pickup?.driverSignature?.attachments?.forEach((a) => allAtts.push({ source: "Pickup Driver", name: a.originalName || "-" }));
  delivery?.driverSignature?.attachments?.forEach((a) => allAtts.push({ source: "Delivery Driver", name: a.originalName || "-" }));
  delivery?.customerSignature?.attachments?.forEach((a) => allAtts.push({ source: "Consignee", name: a.originalName || "-" }));

  if (allAtts.length) {
    ensureSpace(40 + allAtts.length * 11);
    drawText(ctx, "PROOF ATTACHMENTS", { x: margin, y, size: 9, bold: true });
    y -= 14;
    for (const a of allAtts) {
      ensureSpace(14);
      drawText(ctx, `* [${a.source}] ${a.name}`, { x: margin + 4, y, size: 8, maxWidth: width - 2 * margin - 8 });
      y -= 11;
    }
    y -= 6;
  }

  // ── Manual status advancements ──
  // Steps an office user advanced manually, bypassing a POH signature/file.
  // Without this, the document looks "completed" with unexplained missing
  // signatures. Listing them makes the gap auditable.
  const overrides = Array.isArray(shipment?.pohStatusOverrides)
    ? shipment.pohStatusOverrides
    : [];
  if (overrides.length) {
    ensureSpace(46 + overrides.length * 22);
    drawText(ctx, "MANUAL STATUS ADVANCEMENTS", {
      x: margin,
      y,
      size: 9,
      bold: true,
      color: COLORS.amber,
    });
    y -= 13;
    drawText(
      ctx,
      "These steps were advanced manually by office staff; the related signature/file was not captured.",
      { x: margin, y, size: 7, color: COLORS.muted, maxWidth: width - 2 * margin },
    );
    y -= 16;
    for (const o of overrides) {
      ensureSpace(24);
      const label = OVERRIDE_LABELS[o.reason ?? ""] || o.reason || "Manual advance";
      drawPill(ctx, "MANUAL", margin, y, COLORS.amberSoft, COLORS.amber, 7);
      drawText(ctx, label, {
        x: margin + 52,
        y,
        size: 8,
        bold: true,
        maxWidth: width - 2 * margin - 52,
      });
      y -= 11;
      const meta = [o.byName, o.at ? fmtDt(o.at) : null].filter(Boolean).join(" * ");
      if (meta) {
        drawText(ctx, meta, { x: margin + 52, y, size: 7, color: COLORS.muted });
        y -= 11;
      }
      y -= 3;
    }
    y -= 6;
  }

  // ── Footer ──
  ctx.page.drawLine({
    start: { x: margin, y: 50 },
    end: { x: width - margin, y: 50 },
    thickness: 0.5,
    color: COLORS.line,
  });
  drawText(
    ctx,
    `Generated ${new Date().toLocaleString()} * LiveCargo - Proof of Handover * Document is immutable once both events are locked.`,
    { x: margin, y: 38, size: 7, color: COLORS.faint, maxWidth: width - 2 * margin },
  );

  return pdfDoc.save();
}

export async function downloadPOHDocumentPdf(data: PohData) {
  const bytes = await createPOHDocumentPdf(data);
  const blob = new Blob([bytes as unknown as BlobPart], { type: "application/pdf" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  // Use shipmentNumber when available for a more recognisable filename
  const fileBase = data.shipmentId?.shipmentNumber
    ? `POH-${data.shipmentId.shipmentNumber}`
    : pohRef(data._id);
  a.download = `${fileBase}.pdf`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
